import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Search, Sparkles, Loader2, LogIn, LayoutGrid, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import nexusLogo from '@/assets/nexus-logo-160.webp';

type Mode = 'simple' | 'ai';

const container: Variants = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item: Variants = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 24 } } };

type Segment = 'b2c' | 'b2b';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const PRODUCTS_SELECT = 'id,name,description,price,images,category,stock,store_id,min_order_quantity,wholesale_price,stores!inner(slug,name,is_published,store_type)';

const supabaseRequest = async (path: string, init: RequestInit = {}) => {
  const response = await fetch(`${SUPABASE_URL}${path}`, {
    ...init,
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
      'Content-Type': 'application/json',
      ...init.headers,
    },
  });
  if (!response.ok) throw new Error(`Supabase request failed (${response.status})`);
  return response.json();
};

const getProductImageSources = (url: string) => {
  if (!/_lg\.webp(?:\?.*)?$/i.test(url)) return null;
  return {
    src: url.replace(/_lg\.webp(?=\?|$)/i, '_md.webp'),
    srcSet: [
      `${url.replace(/_lg\.webp(?=\?|$)/i, '_sm.webp')} 200w`,
      `${url.replace(/_lg\.webp(?=\?|$)/i, '_md.webp')} 400w`,
      `${url} 800w`,
    ].join(', '),
  };
};

const ProductGridSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" aria-hidden="true">
    {Array.from({ length: 10 }, (_, index) => (
      <div key={index} className="glass rounded-2xl overflow-hidden">
        <div className="w-full aspect-square bg-muted/40 shimmer" />
        <div className="p-3 space-y-2">
          <div className="h-3 w-1/2 rounded bg-muted/60" />
          <div className="h-4 w-5/6 rounded bg-muted/60" />
          <div className="h-4 w-1/3 rounded bg-muted/60" />
        </div>
      </div>
    ))}
  </div>
);

const Marketplace: React.FC = () => {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [mode, setMode] = useState<Mode>('simple');
  const [segment, setSegment] = useState<Segment>(() => (localStorage.getItem('mp-segment') as Segment) || 'b2c');
  const [aiResults, setAiResults] = useState<any[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [category, setCategory] = useState<string>('all');
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    localStorage.setItem('mp-segment', segment);
    setAiResults(null);
    setCategory('all');
    setCategoriesOpen(false);
    setCategoryQuery('');
  }, [segment]);

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['mp-products', segment],
    queryFn: async () => {
      const params = new URLSearchParams({
        select: PRODUCTS_SELECT,
        'is_active': 'eq.true',
        'stores.is_published': 'eq.true',
        'stores.store_type': `eq.${segment}`,
        order: 'created_at.desc',
        limit: '500',
      });
      return supabaseRequest(`/rest/v1/products?${params.toString()}`);
    },
  });

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    products.forEach((p: any) => {
      if (p.category) counts.set(p.category, (counts.get(p.category) || 0) + 1);
    });
    return Array.from(counts, ([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [products]);

  const visibleCategories = useMemo(() => {
    const q = categoryQuery.trim().toLowerCase();
    return q ? categories.filter(c => c.name.toLowerCase().includes(q)) : categories;
  }, [categories, categoryQuery]);

  const popularCategories = useMemo(() => {
    const popular = categories.slice(0, 7);
    const selected = categories.find(c => c.name === category);
    return selected && !popular.some(c => c.name === selected.name)
      ? [...popular.slice(0, 6), selected]
      : popular;
  }, [categories, category]);

  const filteredProducts = useMemo(() => {
    let base: any[];
    if (mode === 'ai' && aiResults) {
      const byId = new Map(products.map((p: any) => [p.id, p]));
      base = aiResults
        .map((r: any) => {
          const b = byId.get(r.id);
          return b ? { ...b, reason: r.reason } : null;
        })
        .filter(Boolean) as any[];
    } else if (!debounced) {
      base = products;
    } else {
      const q = debounced.toLowerCase();
      base = products.filter((p: any) =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.stores?.name?.toLowerCase().includes(q)
      );
    }
    if (category !== 'all') base = base.filter((p: any) => p.category === category);
    return base;
  }, [mode, aiResults, debounced, products, category]);


  const runAiSearch = async () => {
    if (!query.trim()) return;
    setAiLoading(true);
    setAiResults(null);
    try {
      const data = await supabaseRequest('/functions/v1/marketplace-ai-search', {
        method: 'POST',
        body: JSON.stringify({ query: query.trim(), segment }),
      });
      setAiResults(data?.results || []);
    } catch (e) {
      setAiResults([]);
    } finally {
      setAiLoading(false);
    }
  };

  const goToProduct = (p: any) => {
    const slug = p.stores?.slug;
    if (slug) navigate(`/store/${slug}/p/${p.id}`);
  };

  return (
    <main className="min-h-screen relative aurora-mesh overflow-hidden" aria-labelledby="marketplace-heading">
      {/* Animated aurora blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)' }}
          animate={{ x: [0, 80, -40, 0], y: [0, 60, -30, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, hsl(var(--secondary) / 0.25), transparent 70%)' }}
          animate={{ x: [0, -60, 30, 0], y: [0, -40, 50, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, hsl(var(--accent) / 0.22), transparent 70%)' }}
          animate={{ x: [0, 40, -50, 0], y: [0, -30, 20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Top bar */}
      <header className="glass sticky top-0 z-40 border-0 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 group">
            <motion.img
              src={nexusLogo}
              alt="Nexus"
              width={36}
              height={36}
              className="w-9 h-9 rounded-xl"
              whileHover={{ rotate: 8, scale: 1.08 }}
              transition={{ type: 'spring', stiffness: 300 }}
            />
            <span className="text-xl font-display font-bold gradient-text">Nexus Marketplace</span>
          </Link>

          <div className="flex items-center gap-2">
            {/* B2C / B2B mode */}
            <div className="inline-flex gap-1 p-1 rounded-xl glass">
              {(['b2c', 'b2b'] as Segment[]).map(s => (
                <button
                  key={s}
                  onClick={() => setSegment(s)}
                  className={cn(
                    'px-3 py-1 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all',
                    segment === s ? 'gradient-primary text-primary-foreground glow-primary' : 'text-muted-foreground hover:text-foreground'
                  )}
                  aria-pressed={segment === s}
                >
                  {s === 'b2c' ? 'B2C' : 'B2B'}
                </button>
              ))}
            </div>
            <Link to="/auth" className="px-3 py-1.5 rounded-xl text-sm font-medium glass glass-hover flex items-center gap-1.5">
              <LogIn className="w-3.5 h-3.5" /> Espace pro
            </Link>
          </div>
        </div>
      </header>

      <motion.div variants={container} initial="hidden" animate="show" className="relative z-10">
        {/* Hero + search */}
        <section className="max-w-4xl mx-auto px-4 pt-16 pb-10 text-center">
          <motion.div variants={item} className="flex justify-center mb-6">
            <motion.img
              src={nexusLogo}
              alt="Nexus"
              width={80}
              height={80}
              fetchPriority="high"
              className="w-20 h-20 rounded-2xl glass p-2"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
          <motion.h1 id="marketplace-heading" variants={item} className="text-4xl md:text-5xl font-display font-bold text-foreground mb-3">
            {segment === 'b2b' ? (
              <>Marketplace <span className="gradient-text">Professionnelle</span> · B2B</>
            ) : (
              <>Découvre <span className="gradient-text">toutes les boutiques</span> en un seul endroit</>
            )}
          </motion.h1>
          <motion.p variants={item} className="text-muted-foreground mb-8">
            {segment === 'b2b'
              ? 'Produits en gros pour professionnels. Boutiques et catalogues réservés aux acheteurs pro.'
              : "Cherche un produit, on t'emmène directement dans la bonne boutique."}
          </motion.p>

          <motion.div variants={item} className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
            <input
              type="search"
              aria-label="Rechercher dans la marketplace"
              value={query}
              onChange={e => { setQuery(e.target.value); if (mode === 'ai') setAiResults(null); }}
              onKeyDown={e => e.key === 'Enter' && mode === 'ai' && runAiSearch()}
              placeholder={mode === 'ai' ? 'Décris ce que tu cherches en langage naturel…' : 'Rechercher un produit, une catégorie, une boutique…'}
              className="w-full pl-14 pr-32 py-4 rounded-2xl glass text-foreground text-base outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
            />
            {mode === 'ai' && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={runAiSearch}
                disabled={aiLoading || !query.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl gradient-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5 glow-primary"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Chercher
              </motion.button>
            )}
          </motion.div>

          {/* Mode toggle */}
          <motion.div variants={item} className="mt-4 inline-flex gap-1 p-1 rounded-xl glass">
            <button
              onClick={() => { setMode('simple'); setAiResults(null); }}
              className={cn('px-4 py-1.5 rounded-lg text-sm font-medium transition-all', mode === 'simple' ? 'gradient-primary text-primary-foreground glow-primary' : 'text-muted-foreground hover:text-foreground')}
            >
              <Search className="w-3.5 h-3.5 inline mr-1" /> Recherche simple
            </button>
            <button
              onClick={() => setMode('ai')}
              className={cn('px-4 py-1.5 rounded-lg text-sm font-medium transition-all', mode === 'ai' ? 'gradient-primary text-primary-foreground glow-primary' : 'text-muted-foreground hover:text-foreground')}
            >
              <Sparkles className="w-3.5 h-3.5 inline mr-1" /> Recherche IA
            </button>
          </motion.div>
        </section>

        {/* Products */}
        <section className="max-w-7xl mx-auto px-4 pb-16" aria-labelledby="products-heading" aria-busy={productsLoading}>
          <motion.h2 id="products-heading" variants={item} className="text-lg font-display font-semibold text-foreground mb-4">
            {productsLoading ? 'Chargement des produits…' :
             mode === 'ai' && aiResults ? `Résultats IA (${filteredProducts.length})` :
             debounced ? `Résultats pour "${debounced}" (${filteredProducts.length})` :
             `Tous les produits (${filteredProducts.length})`}
          </motion.h2>

          {productsLoading && (
            <div className="mb-7 h-[72px] rounded-2xl glass p-4" aria-hidden="true">
              <div className="h-10 w-full rounded-xl bg-muted/40 shimmer" />
            </div>
          )}

          {!productsLoading && categories.length > 0 && (
            <motion.div variants={item} className="mb-7 rounded-2xl glass p-3 sm:p-4">
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
                <button
                  onClick={() => setCategory('all')}
                  className={cn(
                    'shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all',
                    category === 'all' ? 'gradient-primary text-primary-foreground glow-primary' : 'bg-background/30 text-muted-foreground hover:text-foreground hover:bg-background/50'
                  )}
                >
                  Toutes <span className="ml-1 opacity-70">{products.length}</span>
                </button>
                <button
                  onClick={() => setCategoriesOpen(open => !open)}
                  className="shrink-0 px-4 py-2 rounded-xl text-sm font-semibold text-foreground bg-background/40 hover:bg-background/60 transition-colors flex items-center gap-2"
                  aria-expanded={categoriesOpen}
                >
                  <LayoutGrid className="w-4 h-4 text-primary" />
                  Catégories
                  <ChevronDown className={cn('w-4 h-4 transition-transform', categoriesOpen && 'rotate-180')} />
                </button>
                {popularCategories.map(c => (
                  <button
                    key={c.name}
                    onClick={() => setCategory(c.name)}
                    className={cn(
                      'shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all',
                      category === c.name ? 'gradient-primary text-primary-foreground glow-primary' : 'bg-background/30 text-muted-foreground hover:text-foreground hover:bg-background/50'
                    )}
                  >
                    {c.name} <span className="ml-1 opacity-60">{c.count}</span>
                  </button>
                ))}
              </div>

              {categoriesOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 pt-3 border-t border-white/10"
                >
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      value={categoryQuery}
                      onChange={e => setCategoryQuery(e.target.value)}
                      placeholder={`Rechercher parmi ${categories.length} catégories…`}
                      className="w-full h-10 pl-10 pr-10 rounded-xl bg-background/30 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                    />
                    {categoryQuery && (
                      <button onClick={() => setCategoryQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Effacer la recherche">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
                    {visibleCategories.map(c => (
                      <button
                        key={c.name}
                        onClick={() => { setCategory(c.name); setCategoriesOpen(false); setCategoryQuery(''); }}
                        className={cn(
                          'flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-left text-sm transition-colors',
                          category === c.name ? 'bg-primary/20 text-primary font-semibold' : 'bg-background/25 text-foreground hover:bg-background/50'
                        )}
                      >
                        <span className="truncate">{c.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{c.count}</span>
                      </button>
                    ))}
                  </div>
                  {visibleCategories.length === 0 && (
                    <p className="py-5 text-center text-sm text-muted-foreground">Aucune catégorie ne correspond.</p>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}

          {aiLoading && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> L'IA analyse le catalogue…
            </div>
          )}

          {!productsLoading && !aiLoading && filteredProducts.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              Aucun produit trouvé{debounced && ` pour "${debounced}"`}.
            </div>
          )}

          {productsLoading ? <ProductGridSkeleton /> : <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredProducts.map((p: any) => (
              <motion.button
                key={p.id}
                variants={item}
                onClick={() => goToProduct(p)}
                whileHover={{ y: -6, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="text-left glass glass-hover rounded-2xl overflow-hidden transition-all"
              >
                {p.images?.[0] ? (
                  <img
                    src={getProductImageSources(p.images[0])?.src || p.images[0]}
                    srcSet={getProductImageSources(p.images[0])?.srcSet}
                    sizes="(min-width: 1280px) 236px, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                    alt={p.name}
                    width={400}
                    height={400}
                    loading="lazy"
                    decoding="async"
                    className="w-full aspect-square object-cover"
                  />
                ) : (
                  <div className="w-full aspect-square bg-muted/30 flex items-center justify-center text-muted-foreground text-xs">Pas d'image</div>
                )}
                <div className="p-3">
                  <div className="text-xs text-muted-foreground line-clamp-1 mb-1">{p.stores?.name}</div>
                  <div className="font-semibold text-sm text-foreground line-clamp-2">{p.name}</div>
                  <div className="mt-2 font-bold gradient-text">
                    {segment === 'b2b' && p.wholesale_price
                      ? `${Number(p.wholesale_price).toFixed(3)} TND`
                      : `${Number(p.price).toFixed(3)} TND`}
                  </div>
                  {segment === 'b2b' && (p.min_order_quantity ?? 1) > 1 && (
                    <div className="text-[10px] text-muted-foreground mt-1">Min. {p.min_order_quantity} unités</div>
                  )}
                  {p.reason && (
                    <div className="mt-2 text-[11px] text-muted-foreground italic border-t border-white/10 pt-2 line-clamp-2">
                      <Sparkles className="w-3 h-3 inline mr-1 text-primary" />{p.reason}
                    </div>
                  )}
                </div>
              </motion.button>
            ))}
          </motion.div>}
        </section>

        <footer className="border-t border-white/10 py-6 text-center text-xs text-muted-foreground">
          <Link to="/landing" className="hover:text-primary">À propos de la plateforme</Link>
        </footer>
      </motion.div>
    </main>
  );
};

export default Marketplace;
